<template>
  <section class="article-overlayed-images-module-component">
    <div class="sa-container">
      <div class="overlayed-images">
        <figure
          v-if="data.left_column?.image?.url"
          ref="leftImageContainerRef"
          class="image-column left-column">
          <div
            ref="leftImageRef"
            class="image-content">
            <img
              :src="data.left_column.image.url"
              :alt="data.left_column.image.alt || ''" />

            <figcaption
              v-if="data.left_column.caption"
              v-html="data.left_column.caption"></figcaption>
          </div>
        </figure>

        <figure
          v-if="data.right_column?.image?.url"
          ref="rightImageContainerRef"
          class="image-column right-column">
          <div
            ref="rightImageRef"
            class="image-content">
            <img
              :src="data.right_column.image.url"
              :alt="data.right_column.image.alt || ''" />

            <figcaption
              v-if="data.right_column.caption"
              v-html="data.right_column.caption"></figcaption>
          </div>
        </figure>
      </div>
    </div>
  </section>
</template>

<script setup>
import useUtils from '@/components/composables/useUtils.js'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

gsap.registerPlugin(ScrollTrigger)

defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const { device } = useUtils()
const leftImageContainerRef = ref(null)
const rightImageContainerRef = ref(null)
const leftImageRef = ref(null)
const rightImageRef = ref(null)
const imageTweens = []
let stopDeviceWatch = null

const clearImageParallax = () => {
  imageTweens.splice(0).forEach((tween) => {
    tween.scrollTrigger?.kill()
    tween.kill()
  })
}

const addImageParallax = (element, trigger, startY, endY) => {
  if (!element || !trigger) return

  gsap.set(element, { y: startY })

  imageTweens.push(
    gsap.to(element, {
      y: endY,
      ease: 'none',
      scrollTrigger: {
        trigger,
        scrub: true,
        start: 'top bottom',
        end: 'center center',
      },
    })
  )
}

const setupImageParallax = async () => {
  await nextTick()
  clearImageParallax()

  const rightEndY = device.value === 'desktop' ? -50 : 0

  addImageParallax(leftImageRef.value, leftImageContainerRef.value, -40, 0)
  addImageParallax(
    rightImageRef.value,
    rightImageContainerRef.value,
    40,
    rightEndY
  )

  ScrollTrigger.refresh()
}

onMounted(() => {
  setupImageParallax()
  stopDeviceWatch = watch(device, setupImageParallax, { flush: 'post' })
})

onBeforeUnmount(() => {
  stopDeviceWatch?.()
  clearImageParallax()
})
</script>

<style lang="scss" scoped>
.article-overlayed-images-module-component {
  @apply relative overflow-hidden bg-white pt-8 lg:pt-10;

  .overlayed-images {
    @apply relative mx-auto flex w-full items-start lg:px-10;

    .image-column {
      @apply m-0 min-w-0;

      .image-content {
        @apply relative;

        img {
          @apply block w-full rounded-[17px] object-cover object-center;
        }

        figcaption {
          @apply mt-2 text-[11px] leading-[1.2] text-black lg:mt-3 lg:text-[13px];
        }
      }

      &.left-column {
        @apply relative z-1 w-[58%];

        img {
          aspect-ratio: 4 / 3;
        }
      }

      &.right-column {
        @apply relative z-2 mt-[24%] ml-[-7%] w-[49%];

        img {
          aspect-ratio: 1 / 1;
        }
      }
    }
  }
}
</style>
